import * as React from "react";
import { Link } from "react-router";
import { CheckCircle2, ChevronRight, Clock, Thermometer } from "lucide-react";
import { cn } from "@/lib/utils";
import { stufeShort } from "@/lib/stufen-labels";
import type { HourlyDueResult } from "./hourly-due-rooms";

/**
 * HourlyMeasureCard — B7, "Stündliche Messung" bei ASR-Stufe Kritisch.
 *
 * Erscheint NUR im seltenen Kritisch-Zustand (≥1 Raum heute auf Kritisch).
 * Teilt die Kritisch-Räume in "diese Stunde noch offen" (mit Schnell-Link zum
 * Messen) vs. "bereits gemessen" auf.
 *
 * Red-Lines (Produkt-Kern — ehrliche, nicht-strafende Unterstützung):
 *   - KEIN Erfüllungs-Counter / Prozent / Score. Die Liste ist eine
 *     Arbeitshilfe ("noch offen: A, B"), keine Bewertung. Personalausfall ≠
 *     Versäumnis-Note.
 *   - Angebot, keine Pflicht-Mechanik: Wortlaut "ASR A3.5 empfiehlt".
 *   - Ruhige Karte: heat-4-Akzent (ehrlich, es IST Kritisch), aber KEIN
 *     Alarm-Puls, keine Animation.
 *   - Advisory/Client-only — fließt nicht in den versiegelten Record.
 *
 * Palette: heat-4-soft Hintergrund + heat-4 für Icon/Heading/Mess-Link.
 * Positive Bestätigung (alles gemessen) in cool-teal (success-akzent).
 */
export function HourlyMeasureCard({
  due,
}: {
  due: HourlyDueResult;
}): React.ReactElement | null {
  // Karte existiert nur im Kritisch-Zustand.
  if (!due.anyKritischToday) return null;

  const { openThisHour, doneThisHour } = due;
  const hasOpen = openThisHour.length > 0;

  return (
    <section
      role="group"
      aria-label="Stündliche Messung bei Stufe Kritisch"
      className={cn(
        "rounded-2xl border border-[color:var(--color-border)]",
        "bg-[color:var(--color-heat-4-soft)] p-5 shadow-[var(--shadow-md)] md:p-6",
      )}
    >
      {/* Header — heat-4 (ehrlich kritisch), aber ruhig (kein Puls) */}
      <header className="flex items-start gap-2.5">
        <Clock
          aria-hidden="true"
          strokeWidth={2.25}
          className="mt-0.5 size-5 shrink-0 text-[color:var(--color-heat-4)]"
        />
        <div className="min-w-0">
          <h2 className="text-sm font-semibold text-[color:var(--color-heat-4)]">
            Stündliche Messung – Stufe {stufeShort(3)}
          </h2>
          <p className="mt-1 text-sm leading-snug text-[color:var(--color-text-secondary)]">
            ASR A3.5 empfiehlt bei Stufe Kritisch (über 35 °C gemessen) eine
            stündliche Messung. Diese Liste ist eine Arbeitshilfe – keine
            Bewertung.
          </p>
        </div>
      </header>

      <div className="mt-4">
        {hasOpen ? (
          <>
            <p className="text-xs font-semibold uppercase tracking-[var(--letter-spacing-wide)] text-[color:var(--color-text-secondary)]">
              Diese Stunde noch offen
            </p>
            <ul className="mt-2 space-y-2">
              {openThisHour.map((room) => (
                <li
                  key={room.room_id}
                  className="flex items-center justify-between gap-3 rounded-lg border border-[color:var(--color-border)] bg-[color:var(--color-surface)] px-3"
                >
                  <span className="min-w-0 truncate py-3 text-sm font-medium text-[color:var(--color-text-primary)]">
                    {room.name}
                  </span>
                  <Link
                    to={`/entries/neu?room=${room.room_id}`}
                    aria-label={`${room.name} jetzt messen`}
                    className={cn(
                      "-mr-1 inline-flex min-h-[var(--size-touch-primary)] shrink-0 items-center gap-1 rounded-md px-2",
                      "text-sm font-semibold text-[color:var(--color-heat-4)]",
                      "underline-offset-4 hover:underline",
                      "focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[color:var(--color-border-focus)]",
                    )}
                  >
                    <Thermometer aria-hidden="true" strokeWidth={2.25} className="size-4" />
                    messen
                    <ChevronRight aria-hidden="true" strokeWidth={2.25} className="size-3.5" />
                  </Link>
                </li>
              ))}
            </ul>
            {doneThisHour.length > 0 && (
              <p className="mt-3 text-xs leading-snug text-[color:var(--color-text-secondary)]">
                Bereits gemessen diese Stunde:{" "}
                {doneThisHour.map((r) => r.name).join(", ")}.
              </p>
            )}
          </>
        ) : (
          // openThisHour === 0 ⇒ alle Kritisch-Räume diese Stunde gemessen.
          // Positiv, kein Score. role=status für ruhige SR-Bestätigung.
          <div
            role="status"
            className="flex items-center gap-2 rounded-lg bg-[color:var(--color-cool-teal-50)] px-3 py-3"
          >
            <CheckCircle2
              aria-hidden="true"
              strokeWidth={2.25}
              className="size-5 shrink-0 text-[color:var(--color-cool-teal-600)]"
            />
            <p className="text-sm font-medium text-[color:var(--color-cool-teal-900)]">
              Alle Kritisch-Räume diese Stunde gemessen
            </p>
          </div>
        )}
      </div>
    </section>
  );
}

HourlyMeasureCard.displayName = "HourlyMeasureCard";
